import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import * as Updates from 'expo-updates';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppState, Linking, Modal, Platform, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { FadeIn, ZoomIn } from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { useThemePalette } from '@/config/theme';
import { useAppConfig } from '@/features/app/useAppConfig';

const ANDROID_PACKAGE = 'com.tashkeela.app';
const MARKET_URL = `market://details?id=${ANDROID_PACKAGE}`;
const WEB_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

/**
 * Robust dotted-numeric semver compare. Splits on '.', compares each numeric
 * segment, and pads the shorter side with zeros so "2.1" == "2.1.0". Crucially
 * NUMERIC, not string, so 2.1.10 > 2.1.9 (a lexical compare would get this
 * wrong). Non-numeric / malformed segments collapse to 0, so a garbage value can
 * never accidentally trigger a block. Returns -1 / 0 / 1 for a<b / a==b / a>b.
 */
export function compareVersions(a: string, b: string): number {
  const pa = String(a).split('.');
  const pb = String(b).split('.');
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const na = Number.parseInt(pa[i] ?? '0', 10) || 0;
    const nb = Number.parseInt(pb[i] ?? '0', 10) || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}

/** The installed STORE build version (app.json `version`), e.g. "2.1.5". */
function installedVersion(): string {
  const v = Constants.expoConfig?.version;
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : '0.0.0';
}

type GateMode = 'required' | 'available' | 'ota' | null;

/**
 * Glassify-style "Update available / Update required" gate.
 *
 * On launch it compares the INSTALLED store version against the server config
 * (app_config via get_app_config):
 *   - installed < min_version  OR  force_update  -> BLOCKING "Update required"
 *     modal: no dismiss, no scrim-close, one "Update" button -> Play Store.
 *   - installed < latest_version -> dismissible "Update available" nudge
 *     (Update + Later).
 * As a secondary path, a downloaded OTA (`isUpdatePending`) shows its own soft
 * nudge whose button reloads onto the new JS bundle.
 *
 * Premium glass card, bilingual + RTL via i18n. No-ops safely on web and in dev
 * / Expo Go (Updates.isEnabled false; store links unavailable) — the store
 * version check still runs natively, but the modal simply won't fire if config
 * keeps SAFE 0.0.0 defaults.
 */
export function AppUpdateGate() {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const { data: config } = useAppConfig();
  const { isUpdatePending } = Updates.useUpdates();

  const [otaDismissed, setOtaDismissed] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  // The store-version decision is stable for the session (config + installed
  // version don't change at runtime); recompute only when config arrives.
  const storeMode = useMemo<Exclude<GateMode, 'ota' | null> | null>(() => {
    if (Platform.OS === 'web' || !config) return null;
    const installed = installedVersion();
    if (config.forceUpdate || compareVersions(installed, config.minVersion) < 0) {
      return 'required';
    }
    if (compareVersions(installed, config.latestVersion) < 0) return 'available';
    return null;
  }, [config]);

  // Re-check for a newer OTA each time the app returns to the foreground, so a
  // long-lived session still discovers + downloads a fresh bundle (fetching it
  // flips isUpdatePending, which re-arms the OTA nudge).
  useEffect(() => {
    if (!Updates.isEnabled) return;
    const sub = AppState.addEventListener('change', (state) => {
      if (state !== 'active') return;
      Updates.checkForUpdateAsync()
        .then((res) => (res.isAvailable ? Updates.fetchUpdateAsync() : null))
        .catch(() => {
          // Offline / transient — try again on the next foreground.
        });
    });
    return () => sub.remove();
  }, []);

  // The store gate wins over the OTA nudge: a required/available store update is
  // more important than reloading an OTA (and the OTA auto-applies on next cold
  // launch anyway). OTA nudge only shows when there's no store action pending.
  const mode: GateMode = useMemo(() => {
    if (storeMode === 'required') return 'required';
    if (storeMode === 'available' && !nudgeDismissed) return 'available';
    if (Updates.isEnabled && isUpdatePending && !otaDismissed) return 'ota';
    return null;
  }, [storeMode, nudgeDismissed, isUpdatePending, otaDismissed]);

  const openStore = useCallback(async () => {
    setBusy(true);
    try {
      await Linking.openURL(MARKET_URL);
    } catch {
      try {
        await Linking.openURL(WEB_STORE_URL);
      } catch {
        // No Play app / browser — nothing more to do; the gate re-offers later.
      }
    }
    setBusy(false);
  }, []);

  const reloadOta = useCallback(async () => {
    setBusy(true);
    try {
      await Updates.reloadAsync();
    } catch {
      // Reload failed (rare) — leave the nudge so the user can retry.
      setBusy(false);
    }
  }, []);

  if (mode === null) return null;

  const isBlocking = mode === 'required';
  const copy =
    mode === 'required'
      ? { title: t('updateGate.required.title'), body: t('updateGate.required.body') }
      : mode === 'available'
        ? { title: t('updateGate.available.title'), body: t('updateGate.available.body') }
        : { title: t('updateGate.ota.title'), body: t('updateGate.ota.body') };

  const primaryLabel = busy
    ? mode === 'ota'
      ? t('updateGate.reloading')
      : t('updateGate.opening')
    : mode === 'ota'
      ? t('updateGate.reloadCta')
      : t('updateGate.cta');

  const onPrimary = mode === 'ota' ? reloadOta : openStore;
  const dismiss = mode === 'ota' ? () => setOtaDismissed(true) : () => setNudgeDismissed(true);
  // Blocking modals can never be dismissed (no scrim-close, no Later, no back).
  const onDismiss = isBlocking ? undefined : dismiss;

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
      statusBarTranslucent
    >
      <Animated.View
        entering={FadeIn.duration(180)}
        className="flex-1 items-center justify-center px-6"
        style={{ backgroundColor: 'rgba(0,0,0,0.72)' }}
      >
        {/* Scrim taps defer a dismissible nudge; a blocking modal ignores them. */}
        <Pressable
          className="absolute inset-0"
          accessibilityRole="button"
          onPress={!isBlocking && !busy ? onDismiss : undefined}
        />
        <Animated.View
          entering={ZoomIn.springify().damping(16)}
          className="w-full items-center rounded-3xl border border-volt-line bg-surface-2 px-5 pb-5 pt-6"
          style={{ maxWidth: 360 }}
        >
          <View className="h-16 w-16 items-center justify-center rounded-full bg-volt-glass-strong">
            <Ionicons
              name={isBlocking ? 'rocket' : 'sparkles'}
              size={28}
              color={palette.voltBright}
            />
          </View>
          <AppText variant="title" weight="bold" className="mt-3 text-center text-ink">
            {copy.title}
          </AppText>
          <AppText variant="caption" className="mt-2 text-center text-soft">
            {copy.body}
          </AppText>
          <View className="mt-5 w-full">
            <Button label={primaryLabel} variant="primary" loading={busy} onPress={onPrimary} />
          </View>
          {!isBlocking ? (
            <Pressable
              accessibilityRole="button"
              onPress={onDismiss}
              disabled={busy}
              className="mt-2 items-center py-2"
            >
              <AppText variant="micro" className="text-muted">
                {t('updateGate.later')}
              </AppText>
            </Pressable>
          ) : null}
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}
